import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import toast from "react-hot-toast";
import { useGetInvoiceByName } from "../invoice-processing-queue/useGetInvoiceByName";
import { useGetDevices } from "../device-settings/useGetDevices";
import { useGetCompany } from "../company-settings/useGetCompany";
import { useSaveDraft } from "../invoice-creation-wizard/useSaveDraft";
import { useClearInvoice } from "../invoice-creation-wizard/useClearInvoice";
import { addNewInvoice } from "pages/invoice-processing-queue/invoiceProgressSlice";
import { saveInvoiceProgress } from "api/invoice.api";
import { useDispatch } from "react-redux";
import { ROUTES } from "config/routes";
// ZATCA Validation Rules
const ZATCA_VALIDATION_RULES = {
  vatNumber: {
    pattern: /^3\d{14}$/,
    message: "VAT number must be 15 digits starting and ending with 3",
  },
  crNumber: {
    pattern: /^\d{10}$/,
    message: "CR number must be exactly 10 digits",
  },
  postalCode: {
    pattern: /^\d{5}$/,
    message: "Postal code must be exactly 5 digits",
  },
  buildingNumber: {
    pattern: /^\d{4}$/,
    message: "Building number must be exactly 4 digits",
  },
};

// Helper function to get exemption reason text
const getExemptionReasonText = (code) => {
  const exemptionReasons = {
    "VATEX-SA-32": "Export of goods",
    "VATEX-SA-33": "Export of services",
    "VATEX-SA-34-1": "The international transport of Goods",
    "VATEX-SA-34-2": "International transport of passengers",
    "VATEX-SA-34-3":
      "Services directly connected and incidental to a Supply of international passenger transport",
    "VATEX-SA-34-4": "Supply of a qualifying means of transport",
    "VATEX-SA-34-5":
      "Any services relating to Goods or passenger transportation",
    "VATEX-SA-35": "Medicines and medical equipment",
    "VATEX-SA-36": "Qualifying metals",
    "VATEX-SA-EDU": "Private education to citizen",
    "VATEX-SA-HEA": "Private healthcare to citizen",
    "VATEX-SA-29":
      "Financial services mentioned in Article 29 of the VAT Regulations",
    "VATEX-SA-30":
      "Life insurance services mentioned in Article 30 of the VAT Regulations",
    "VATEX-SA-29-7":
      "Supply of international finance lease of Commercial Aircraft",
    "VATEX-SA-29-8": "Supply of finance lease of large machinery and equipment",
    "VATEX-SA-29-9": "Supply of finance lease of Commercial Vessels",
    "VATEX-SA-OOS": "Out of scope of VAT",
  };
  return exemptionReasons[code] || "";
};

const InvoiceErrorCorrectionContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");

  // Get invoice name from navigation state.
  // This page is reached by clicking an invoice in the processing queue
  // (View Details / Correct Error), which passes { invoiceName, batchId }.
  // If opened with no context (e.g. the header nav link), we render a
  // friendly empty state below instead of crashing.
  const { invoiceName, batchId } = location.state ?? {};

  // Fetch data
  const { data: invoiceData, isLoading: isLoadingInvoice } =
    useGetInvoiceByName(invoiceName);
  const { data: devicesData } = useGetDevices();
  const { data: company } = useGetCompany();
  const { mutate: saveDraft, isLoading: isSubmitting } = useSaveDraft();
  const { mutate: clearInvoice } = useClearInvoice();

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [formData, setFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [backendErrors, setBackendErrors] = useState(null);
  const [sequentialInvoiceDetails, setSequentialInvoiceDetails] =
    useState(null);

  // Initialize form data when invoice loads
  useEffect(() => {
    if (invoiceData && company) {
      setFormData({
        deviceId:
          selectedDevice?._id ||
          selectedDevice?.id ||
          selectedDevice?.deviceId ||
          "",

        invoiceHeader: {
          issueDate:
            invoiceData.invoiceHeader?.issueDate ||
            new Date().toISOString().split("T")[0],
          issueTime:
            invoiceData.invoiceHeader?.issueTime ||
            new Date().toTimeString().split(" ")[0],
          invoiceTypeCode: invoiceData.invoiceHeader?.invoiceTypeCode || "388",
          invoiceTypeTransaction:
            invoiceData.invoiceHeader?.invoiceTypeTransaction || "0100000",
          notes: invoiceData.invoiceHeader?.notes || [],
          documentCurrencyCode:
            invoiceData.invoiceHeader?.documentCurrencyCode || "SAR",
          taxCurrencyCode: "SAR",
          invoiceReferenceNumber: invoiceData?.InvoiceReferenceNumber,
        },
        processControl: {
          profileId: "reporting:1.0",
        },
        seller: {
          vatNumber: company?.vatNumber || "",
          registrationNumber: {
            id: company?.registrationNumber?.id || "",
            schemeId: company?.registrationNumber?.schemeId || "CRN",
          },
          registrationName: company?.legalName || "",
          address: {
            streetName: company?.address?.street || "",
            additionalStreetName:
              invoiceData.seller?.address?.additionalStreetName || "",
            buildingNumber: company?.address?.buildingNumber || "",
            plotIdentification: company?.address?.plotIdentification || "",
            cityName: company?.address?.city || "",
            citySubdivisionName: company?.address?.district || "",
            countrySubentity: company?.address?.countrySubentity || "",
            postalZone: company?.address?.postalCode || "",
            countryCode: company?.address?.countryCode || "SA",
          },
          otherIdentifiers:
            invoiceData.seller?.otherIdentifiers ||
            company?.otherIdentifiers ||
            [],
        },
        buyer: {
          vatNumber: invoiceData.buyer?.vatNumber || "",
          registrationNumber: {
            id: invoiceData.buyer?.registrationNumber?.id || "",
            schemeId: "CRN",
          },
          registrationName: invoiceData.buyer?.registrationName || "",
          address: {
            streetName: invoiceData.buyer?.address?.street || "",
            additionalStreetName:
              invoiceData.buyer?.address?.additionalStreetName || "",
            buildingNumber: invoiceData.buyer?.address?.buildingNumber || "",
            plotIdentification:
              invoiceData.buyer?.address?.plotIdentification || "",
            cityName: invoiceData.buyer?.address?.city || "",
            citySubdivisionName:
              invoiceData.buyer?.address?.citySubdivisionName || "",
            countrySubentity:
              invoiceData.buyer?.address?.countrySubentity || "",
            postalZone: invoiceData.buyer?.address?.postCode || "",
            countryCode: invoiceData.buyer?.address?.countryCode || "SA",
          },
          otherIdentifiers: invoiceData.buyer?.otherIdentifiers || [],
        },
        delivery: {
          actualDeliveryDate:
            invoiceData.delivery?.actualDeliveryDate ||
            new Date().toISOString().split("T")[0],
          latestDeliveryDate: invoiceData.delivery?.latestDeliveryDate || "",
        },
        paymentMeans: invoiceData.paymentMeans || [
          {
            paymentMeansCode: "30",
            paymentNote: "Payment within 30 days",
            payeeFinancialAccount: {
              iban: company?.iban || "",
            },
          },
        ],
        allowancesCharges: invoiceData.allowancesCharges || [],
        invoiceLines: (invoiceData.invoiceLines || []).map((line, index) => {
          // Calculate lineExtensionAmount (amount without tax)
          const lineExtensionAmount =
            line.lineExtensionAmount ||
            (line.lineTotal
              ? line.lineTotal - (line.taxAmount || 0)
              : (line.quantity || 1) * (line.unitPrice || 0));

          return {
            lineId: line.lineId || `${index + 1}`,
            invoicedQuantity: line.invoicedQuantity || line.quantity || 1,
            unitCode: line.unitCode || "PCE",
            lineExtensionAmount: lineExtensionAmount,
            item: {
              name: line.item?.name || line.itemName || "",
              classifiedTaxCategory: {
                id:
                  line.item?.classifiedTaxCategory?.id ||
                  line.taxCategoryId ||
                  "S",
                percent:
                  line.item?.classifiedTaxCategory?.percent ||
                  line.taxPercent ||
                  15,
              },
            },
            price: {
              priceAmount: line.price?.priceAmount || line.unitPrice || 0,
            },
            taxTotal: {
              taxAmount: line.taxTotal?.taxAmount || line.taxAmount || 0,
              roundingAmount: line.taxTotal?.roundingAmount || 0,
            },
          };
        }),
        invoiceTotals: {
          taxExclusiveAmount: invoiceData?.invoiceTotals?.taxExclusiveAmount,
          taxInclusiveAmount: invoiceData?.invoiceTotals?.taxInclusiveAmount,
          payableAmount: invoiceData?.invoiceTotals?.payableAmount,
        },
        vatBreakdown: (invoiceData?.vatBreakdown || []).map((vat, index) => ({
          ...vat,
          taxableAmount: vat?.taxableAmount,
          taxAmount: vat?.taxAmount,
          vatCategoryCode: vat?.taxCategory,
          vatRate: vat?.taxPercent || "15%",
          exemptionReason: vat?.expemtionReason || "",
          exemptionReasonCode:
            invoiceData?.vatBreakdown?.exemptionReasonCode || "",
        })),
        // [
        //   {
        //     taxableAmount: invoiceData?.vatBreakdown?.taxableAmount,
        //     taxAmount: invoiceData?.vatBreakdown?.taxAmount,
        //     vatCategoryCode: invoiceData?.vatBreakdown?.taxCategory,
        //     vatRate: invoiceData?.vatBreakdown?.taxPercent || "15%",
        //     exemptionReason: invoiceData?.vatBreakdown?.expemtionReason || "",
        //     exemptionReasonCode:
        //       invoiceData?.vatBreakdown?.exemptionReasonCode || "",
        //   },
        // ],
      });
    }
  }, [invoiceData, company, selectedDevice]);

  // Auto-select first available device
  useEffect(() => {
    if (devicesData && !selectedDevice) {
      const availableDevices = devicesData?.filter(
        (device) =>
          device.csr?.status === "approved" &&
          device.certificates?.production?.status === "active",
      );
      if (availableDevices.length > 0) {
        setSelectedDevice(availableDevices[0]);
      }
    }
  }, [devicesData, selectedDevice]);

  // Validate all fields
  useEffect(() => {
    if (!formData) return;

    const errors = {};

    // Validate seller VAT number
    if (!formData.seller?.vatNumber) {
      errors.sellerVatNumber = "Seller VAT number is required";
    } else if (
      !ZATCA_VALIDATION_RULES.vatNumber.pattern.test(formData.seller.vatNumber)
    ) {
      errors.sellerVatNumber = ZATCA_VALIDATION_RULES.vatNumber.message;
    }

    // Validate seller CR number
    if (!formData.seller?.registrationNumber?.id) {
      errors.sellerCrNumber = "Seller CR number is required";
    } else if (
      !ZATCA_VALIDATION_RULES.crNumber.pattern.test(
        formData.seller.registrationNumber.id,
      )
    ) {
      errors.sellerCrNumber = ZATCA_VALIDATION_RULES.crNumber.message;
    }

    // Validate seller address fields
    if (!formData.seller?.address?.buildingNumber) {
      errors.sellerBuildingNumber = "Building number is required";
    } else if (
      !ZATCA_VALIDATION_RULES.buildingNumber.pattern.test(
        formData.seller.address.buildingNumber,
      )
    ) {
      errors.sellerBuildingNumber =
        ZATCA_VALIDATION_RULES.buildingNumber.message;
    }

    if (!formData.seller?.address?.postalZone) {
      errors.sellerPostalCode = "Postal code is required";
    } else if (
      !ZATCA_VALIDATION_RULES.postalCode.pattern.test(
        formData.seller.address.postalZone,
      )
    ) {
      errors.sellerPostalCode = ZATCA_VALIDATION_RULES.postalCode.message;
    }

    if (!formData.seller?.address?.streetName)
      errors.sellerStreet = "Street name is required";
    if (!formData.seller?.address?.cityName)
      errors.sellerCity = "City is required";
    if (!formData.seller?.address?.citySubdivisionName)
      errors.sellerCitySubdivision = "City subdivision/district is required";

    // Validate IBAN
    if (!formData.paymentMeans?.[0]?.payeeFinancialAccount?.iban) {
      errors.iban = "IBAN is required";
    }

    // Validate buyer fields
    if (!formData.buyer?.registrationName)
      errors.buyerName = "Buyer name is required";

    // Validate buyer VAT number (now required)
    if (!formData.buyer?.vatNumber) {
      errors.buyerVatNumber = "Buyer VAT number is required";
    } else if (
      !ZATCA_VALIDATION_RULES.vatNumber.pattern.test(formData.buyer.vatNumber)
    ) {
      errors.buyerVatNumber = ZATCA_VALIDATION_RULES.vatNumber.message;
    }

    // Validate buyer CR number
    if (!formData.buyer?.registrationNumber?.id) {
      errors.buyerCrNumber = "Buyer CR number is required";
    } else if (
      !ZATCA_VALIDATION_RULES.crNumber.pattern.test(
        formData.buyer.registrationNumber.id,
      )
    ) {
      errors.buyerCrNumber = ZATCA_VALIDATION_RULES.crNumber.message;
    }

    // Validate buyer address
    if (!formData.buyer?.address?.streetName)
      errors.buyerStreet = "Street name is required";

    if (!formData.buyer?.address?.buildingNumber) {
      errors.buyerBuildingNumber = "Building number is required";
    } else if (
      !ZATCA_VALIDATION_RULES.buildingNumber.pattern.test(
        formData.buyer.address.buildingNumber,
      )
    ) {
      errors.buyerBuildingNumber =
        ZATCA_VALIDATION_RULES.buildingNumber.message;
    }

    if (!formData.buyer?.address?.cityName)
      errors.buyerCity = "City is required";

    if (!formData.buyer?.address?.postalZone) {
      errors.buyerPostalCode = "Postal code is required";
    } else if (
      !ZATCA_VALIDATION_RULES.postalCode.pattern.test(
        formData.buyer.address.postalZone,
      )
    ) {
      errors.buyerPostalCode = ZATCA_VALIDATION_RULES.postalCode.message;
    }

    if (!formData.buyer?.address?.citySubdivisionName)
      errors.buyerCitySubdivision = "City subdivision/district is required";

    // Validate invoice totals
    if (
      !formData.invoiceTotals?.taxExclusiveAmount &&
      formData.invoiceTotals?.taxExclusiveAmount !== 0
    ) {
      errors.taxExclusiveAmount = "Tax exclusive amount is required";
    }

    if (
      !formData.invoiceTotals?.taxInclusiveAmount &&
      formData.invoiceTotals?.taxInclusiveAmount !== 0
    ) {
      errors.taxInclusiveAmount = "Tax inclusive amount is required";
    }

    if (
      !formData.invoiceTotals?.payableAmount &&
      formData.invoiceTotals?.payableAmount !== 0
    ) {
      errors.payableAmount = "Payable amount is required";
    }

    // Validate invoice lines
    formData.invoiceLines?.forEach((line, index) => {
      if (!line.lineId)
        errors[`line${index}Id`] = `Line ${index + 1} ID is required`;
      if (!line.item?.name)
        errors[`line${index}Name`] = `Line ${index + 1} name is required`;
      if (!line.invoicedQuantity || line.invoicedQuantity <= 0) {
        errors[`line${index}Quantity`] =
          `Line ${index + 1} quantity must be greater than 0`;
      }
      if (!line.price?.priceAmount || line.price?.priceAmount < 0) {
        errors[`line${index}Price`] = `Line ${index + 1} price is required`;
      }
    });

    // Validate device selection
    if (!selectedDevice) errors.device = "Please select a device";

    setValidationErrors(errors);
  }, [formData, selectedDevice]);

  // Clear backend errors when form data changes
  useEffect(() => {
    if (backendErrors && formData) {
      setBackendErrors(null);
      // Clear the backend field validation errors
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        // Remove only backend-added errors, keep frontend validation
        if (backendErrors.missingFields) {
          backendErrors.missingFields.forEach(({ field }) => {
            const fieldMap = {
              "buyer.address.citySubdivisionName": "buyerCitySubdivision",
              "buyer.address.streetName": "buyerStreet",
              "buyer.address.cityName": "buyerCity",
              "buyer.address.postalZone": "buyerPostalCode",
              "buyer.address.buildingNumber": "buyerBuildingNumber",
              "buyer.registrationName": "buyerName",
              "buyer.vatNumber": "buyerVatNumber",
              "buyer.registrationNumber.id": "buyerCrNumber",
              "seller.address.citySubdivisionName": "sellerCitySubdivision",
              "seller.address.streetName": "sellerStreet",
              "seller.address.cityName": "sellerCity",
              "seller.address.postalZone": "sellerPostalCode",
              "seller.address.buildingNumber": "sellerBuildingNumber",
              "seller.registrationName": "sellerName",
              "seller.vatNumber": "sellerVatNumber",
              "seller.registrationNumber.id": "sellerCrNumber",
              "paymentMeans.0.payeeFinancialAccount.iban": "iban",
              "invoiceTotals.taxExclusiveAmount": "taxExclusiveAmount",
              "invoiceTotals.taxInclusiveAmount": "taxInclusiveAmount",
              "invoiceTotals.payableAmount": "payableAmount",
            };
            const frontendField = fieldMap[field] || field;
            delete newErrors[frontendField];
          });
        }
        return newErrors;
      });
    }
  }, [formData]);

  const handleSubmit = () => {
    if (Object.keys(validationErrors).length > 0) {
      toast.error(t("invoiceCorrection.toastFixErrors"));
      return;
    }

    if (!selectedDevice) {
      toast.error(t("invoiceCorrection.toastFixErrors"));
      return;
    }

    const payload = {
      ...formData,
      deviceId:
        selectedDevice._id || selectedDevice.id || selectedDevice.deviceId,
      ...(sequentialInvoiceDetails && {
        uuid: sequentialInvoiceDetails.uuid,
        invoiceCounterValue: sequentialInvoiceDetails.invoiceCounterValue,
      }),
    };

    const dispatchData = {
      invoiceName,
      batchId,
      step: "submitted",
    };
    saveDraft(payload, {
      onSuccess: (response) => {
        setBackendErrors(null); // Clear backend errors on success
        toast.success(t("invoiceCorrection.toastSubmitted"));
        clearInvoice(response.invoice.id, {
          onSuccess: () => {
            toast.success(t("invoiceCorrection.toastCleared"));
            // Reflect done state in-session (100%, checkmark)
            dispatch(addNewInvoice(dispatchData));
            // Persist terminal "completed" so this upload drops out of the
            // processing queue (get-progress filters completed) and moves to
            // the Completion Summary. Fire-and-forget; navigation continues.
            saveInvoiceProgress({
              invoices: [{ invoiceName, batchId, step: "completed" }],
            }).catch(() => {});
            navigate(ROUTES.INVOICE_HUB);
          },
          onError: (error) => {
            toast.error(t("invoiceCorrection.toastClearFailed"));
            console.error(error);
          },
        });
      },
      onError: (error) => {
        if (error.uuid && error.invoiceNumber && error.invoiceCounterValue) {
          setSequentialInvoiceDetails({
            uuid: error.uuid,
            invoiceNumber: error.invoiceNumber,
            invoiceCounterValue: error.invoiceCounterValue,
          });
        }

        // Parse validation errors from error message
        const parseValidationErrors = (errorMessage) => {
          const fieldErrors = {};
          const missingFields = [];

          if (errorMessage && typeof errorMessage === "string") {
            // Pattern: "field.path: Error message"
            const errorPattern = /([\w.]+):\s*([^,;]+)/g;
            let match;

            while ((match = errorPattern.exec(errorMessage)) !== null) {
              const fieldPath = match[1].trim();
              const errorMsg = match[2].trim();

              // Convert backend field paths to frontend field names for highlighting
              const fieldMap = {
                "buyer.address.citySubdivisionName": "buyerCitySubdivision",
                "buyer.address.streetName": "buyerStreet",
                "buyer.address.cityName": "buyerCity",
                "buyer.address.postalZone": "buyerPostalCode",
                "buyer.address.buildingNumber": "buyerBuildingNumber",
                "buyer.registrationName": "buyerName",
                "buyer.vatNumber": "buyerVatNumber",
                "buyer.registrationNumber.id": "buyerCrNumber",
                "seller.address.citySubdivisionName": "sellerCitySubdivision",
                "seller.address.streetName": "sellerStreet",
                "seller.address.cityName": "sellerCity",
                "seller.address.postalZone": "sellerPostalCode",
                "seller.address.buildingNumber": "sellerBuildingNumber",
                "seller.registrationName": "sellerName",
                "seller.vatNumber": "sellerVatNumber",
                "seller.registrationNumber.id": "sellerCrNumber",
                "paymentMeans.0.payeeFinancialAccount.iban": "iban",
                "invoiceTotals.taxExclusiveAmount": "taxExclusiveAmount",
                "invoiceTotals.taxInclusiveAmount": "taxInclusiveAmount",
                "invoiceTotals.payableAmount": "payableAmount",
              };

              const frontendField = fieldMap[fieldPath] || fieldPath;
              fieldErrors[frontendField] = errorMsg;
              missingFields.push({ field: fieldPath, message: errorMsg });
            }
          }

          return { fieldErrors, missingFields };
        };

        const { fieldErrors, missingFields } = parseValidationErrors(
          error?.message,
        );

        // Update validation errors to highlight fields
        setValidationErrors((prev) => ({
          ...prev,
          ...fieldErrors,
        }));

        // Capture backend errors for display in validation sidebar
        const errorData = {
          message: error?.message || t("invoiceCorrection.toastSubmitFailed"),
          missingFields: missingFields,
          details: error?.details || error?.errors || null,
          validation: error?.validation || null,
          timestamp: new Date().toLocaleString(),
        };

        setBackendErrors(errorData);
        toast.error(t("invoiceCorrection.toastSubmitFailed"), {
          duration: 6000,
        });
      },
    });
  };

  const updateFormField = (path, value) => {
    setFormData((prev) => {
      const keys = path.split(".");
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const isArrayIndex = /^\d+$/.test(keys[i + 1]);

        if (isArrayIndex) {
          // Handle array
          current[key] = Array.isArray(current[key]) ? [...current[key]] : [];
        } else {
          // Handle object
          current[key] = { ...current[key] };
        }
        current = current[key];
      }

      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const updateLineItem = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      invoiceLines: prev.invoiceLines.map((line, i) => {
        if (i !== index) return line;

        const updatedLine = { ...line };

        // Handle nested field updates
        if (field === "item.name") {
          updatedLine.item = { ...updatedLine.item, name: value };
        } else if (field === "invoicedQuantity") {
          updatedLine.invoicedQuantity = value;
          // Recalculate lineExtensionAmount
          updatedLine.lineExtensionAmount =
            value * (updatedLine.price?.priceAmount || 0);
          // Recalculate tax amount (assuming taxPercent is available)
          const taxPercent =
            updatedLine.item?.classifiedTaxCategory?.percent || 0;
          updatedLine.taxTotal = {
            ...updatedLine.taxTotal,
            taxAmount: (updatedLine.lineExtensionAmount * taxPercent) / 100,
          };
        } else if (field === "price.priceAmount") {
          updatedLine.price = { ...updatedLine.price, priceAmount: value };
          // Recalculate lineExtensionAmount
          updatedLine.lineExtensionAmount =
            (updatedLine.invoicedQuantity || 0) * value;
          // Recalculate tax amount
          const taxPercent =
            updatedLine.item?.classifiedTaxCategory?.percent || 0;
          updatedLine.taxTotal = {
            ...updatedLine.taxTotal,
            taxAmount: (updatedLine.lineExtensionAmount * taxPercent) / 100,
          };
        } else if (field === "item.taxCategory.id") {
          updatedLine.item = {
            ...updatedLine.item,
            classifiedTaxCategory: {
              ...updatedLine.item?.classifiedTaxCategory,
              id: value,
            },
          };
        } else if (field === "item.taxCategory.percent") {
          updatedLine.item = {
            ...updatedLine.item,
            classifiedTaxCategory: {
              ...updatedLine.item?.classifiedTaxCategory,
              percent: value,
            },
          };
          // Recalculate tax amount
          updatedLine.taxTotal = {
            ...updatedLine.taxTotal,
            taxAmount: (updatedLine.lineExtensionAmount * value) / 100,
          };
        } else {
          updatedLine[field] = value;
        }

        return updatedLine;
      }),
    }));
  };

  // No invoice selected (opened without navigation context)
  if (!invoiceName) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md px-6">
          <p className="text-muted-foreground mb-4">
            No invoice selected. Open an invoice from the processing queue to
            review and correct it.
          </p>
          <Button
            variant="default"
            iconName="ArrowLeft"
            iconPosition="left"
            onClick={() => navigate(ROUTES.INVOICE_QUEUE)}
          >
            Go to Processing Queue
          </Button>
        </div>
      </div>
    );
  }

  if (isLoadingInvoice) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {t("invoiceCorrection.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">{t("invoiceCorrection.noData")}</p>
      </div>
    );
  }

  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <Helmet>
        <title>{t("invoiceCorrection.title")} - ZATCA Compliance</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("invoiceCorrection.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("invoiceCorrection.fixFor", { invoiceName })}
              </p>
            </div>
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              onClick={() => navigate(-1)}
            >
              {t("invoiceCorrection.back")}
            </Button>
          </div>

          {/* Error Summary */}
          {hasErrors && (
            <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-error rounded-full"></div>
                <p className="font-semibold text-error">
                  {t("invoiceCorrection.errorCount", {
                    count: Object.keys(validationErrors).length,
                  })}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("invoiceCorrection.fixBeforeSubmit")}
              </p>
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Device Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.deviceSelection")}
              </h2>
              <Select
                label={t("invoiceCorrection.selectDevice")}
                options={devicesData
                  ?.filter(
                    (d) =>
                      d.certificates?.production?.status === "active" &&
                      d.csr?.status === "approved",
                  )
                  .map((d) => ({
                    value: d._id,
                    label: `${d.organizationName} — ${d.organizationUnitName}`,
                  }))}
                value={selectedDevice?._id}
                onChange={(valOrEvent) => {
                  const value = valOrEvent?.target
                    ? valOrEvent.target.value
                    : valOrEvent;
                  setSelectedDevice(
                    devicesData?.find((d) => String(d._id) === String(value)) ||
                      null,
                  );
                }}
                error={validationErrors.device}
              ></Select>
            </motion.div>

            {/* Seller Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.seller")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  value={formData.seller?.registrationName || ""}
                  onChange={(e) =>
                    updateFormField("seller.registrationName", e.target.value)
                  }
                  error={validationErrors.sellerName}
                  required
                  disabled
                />
                <Input
                  label="VAT Number"
                  value={formData.seller?.vatNumber || ""}
                  onChange={(e) =>
                    updateFormField("seller.vatNumber", e.target.value)
                  }
                  error={validationErrors.sellerVatNumber}
                  placeholder="3XXXXXXXXXXXXXX"
                  required
                  className={
                    validationErrors.sellerVatNumber ? "border-error" : ""
                  }
                  disabled
                />
                <Input
                  label="CR Number"
                  value={formData.seller?.registrationNumber?.id || ""}
                  onChange={(e) =>
                    updateFormField(
                      "seller.registrationNumber.id",
                      e.target.value,
                    )
                  }
                  error={validationErrors.sellerCrNumber}
                  placeholder="1234567890"
                  required
                  className={
                    validationErrors.sellerCrNumber ? "border-error" : ""
                  }
                  disabled
                />
                <Input
                  label="Street Name"
                  value={formData.seller?.address?.streetName || ""}
                  onChange={(e) =>
                    updateFormField("seller.address.streetName", e.target.value)
                  }
                  error={validationErrors.sellerStreet}
                  required
                  className={
                    validationErrors.sellerStreet ? "border-error" : ""
                  }
                  disabled
                />
                <Input
                  label="Building Number"
                  value={formData.seller?.address?.buildingNumber || ""}
                  onChange={(e) =>
                    updateFormField(
                      "seller.address.buildingNumber",
                      e.target.value,
                    )
                  }
                  error={validationErrors.sellerBuildingNumber}
                  placeholder="1234"
                  required
                  className={
                    validationErrors.sellerBuildingNumber ? "border-error" : ""
                  }
                  disabled
                />
                <Input
                  label="City"
                  value={formData.seller?.address?.cityName || ""}
                  onChange={(e) =>
                    updateFormField("seller.address.cityName", e.target.value)
                  }
                  error={validationErrors.sellerCity}
                  required
                  className={validationErrors.sellerCity ? "border-error" : ""}
                  disabled
                />
                <Input
                  label="Postal Code"
                  value={formData.seller?.address?.postalZone || ""}
                  onChange={(e) =>
                    updateFormField("seller.address.postalZone", e.target.value)
                  }
                  error={validationErrors.sellerPostalCode}
                  placeholder="12345"
                  required
                  className={
                    validationErrors.sellerPostalCode ? "border-error" : ""
                  }
                  disabled
                />
                <Input
                  label="District"
                  value={formData.seller?.address?.citySubdivisionName || ""}
                  onChange={(e) =>
                    updateFormField(
                      "seller.address.citySubdivisionName",
                      e.target.value,
                    )
                  }
                  error={validationErrors.sellerCitySubdivision}
                  required
                  className={
                    validationErrors.sellerCitySubdivision ? "border-error" : ""
                  }
                  disabled
                />
              </div>
            </motion.div>

            {/* Payment Means Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                Payment Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Payment Method"
                  value={formData.paymentMeans?.[0]?.paymentMeansCode || "30"}
                  onChange={(e) =>
                    updateFormField(
                      "paymentMeans.0.paymentMeansCode",
                      e.target.value,
                    )
                  }
                  options={[
                    { value: "10", label: "Cash" },
                    { value: "30", label: "Credit Transfer" },
                    { value: "42", label: "Payment to Bank Account" },
                    { value: "48", label: "Bank Card" },
                    { value: "1", label: "Not Defined" },
                  ]}
                />
                <Input
                  label="IBAN"
                  value={
                    formData.paymentMeans?.[0]?.payeeFinancialAccount?.iban ||
                    ""
                  }
                  onChange={(e) =>
                    updateFormField(
                      "paymentMeans.0.payeeFinancialAccount.iban",
                      e.target.value,
                    )
                  }
                  placeholder="SA00 0000 0000 0000 0000 0000"
                  error={validationErrors.iban}
                  required
                  className={validationErrors.iban ? "border-error" : ""}
                />
                <div className="md:col-span-2">
                  <Input
                    label="Payment Note"
                    value={formData.paymentMeans?.[0]?.paymentNote || ""}
                    onChange={(e) =>
                      updateFormField(
                        "paymentMeans.0.paymentNote",
                        e.target.value,
                      )
                    }
                    placeholder="Payment within 30 days"
                  />
                </div>
              </div>
            </motion.div>

            {/* Buyer Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.buyer")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Customer Name"
                  value={formData.buyer?.registrationName || ""}
                  onChange={(e) =>
                    updateFormField("buyer.registrationName", e.target.value)
                  }
                  error={validationErrors.buyerName}
                  required
                  className={validationErrors.buyerName ? "border-error" : ""}
                />
                <Input
                  label="VAT Number"
                  value={formData.buyer?.vatNumber || ""}
                  onChange={(e) =>
                    updateFormField("buyer.vatNumber", e.target.value)
                  }
                  error={validationErrors.buyerVatNumber}
                  placeholder="3XXXXXXXXXXXXXX"
                  required
                  className={
                    validationErrors.buyerVatNumber ? "border-error" : ""
                  }
                />
                <Input
                  label="CR Number"
                  value={formData.buyer?.registrationNumber?.id || ""}
                  onChange={(e) =>
                    updateFormField(
                      "buyer.registrationNumber.id",
                      e.target.value,
                    )
                  }
                  placeholder="1234567890"
                  error={validationErrors.buyerCrNumber}
                  required
                  className={
                    validationErrors.buyerCrNumber ? "border-error" : ""
                  }
                />
                <Input
                  label="Street Name"
                  value={formData.buyer?.address?.streetName || ""}
                  onChange={(e) =>
                    updateFormField("buyer.address.streetName", e.target.value)
                  }
                  error={validationErrors.buyerStreet}
                  required
                  className={validationErrors.buyerStreet ? "border-error" : ""}
                />
                <Input
                  label="Building Number"
                  value={formData.buyer?.address?.buildingNumber || ""}
                  onChange={(e) =>
                    updateFormField(
                      "buyer.address.buildingNumber",
                      e.target.value,
                    )
                  }
                  placeholder="1234"
                  error={validationErrors.buyerBuildingNumber}
                  required
                  className={
                    validationErrors.buyerBuildingNumber ? "border-error" : ""
                  }
                />
                <Input
                  label="City"
                  value={formData.buyer?.address?.cityName || ""}
                  onChange={(e) =>
                    updateFormField("buyer.address.cityName", e.target.value)
                  }
                  error={validationErrors.buyerCity}
                  required
                  className={validationErrors.buyerCity ? "border-error" : ""}
                />
                <Input
                  label="Postal Code"
                  value={formData.buyer?.address?.postalZone || ""}
                  onChange={(e) =>
                    updateFormField("buyer.address.postalZone", e.target.value)
                  }
                  error={validationErrors.buyerPostalCode}
                  required
                  className={
                    validationErrors.buyerPostalCode ? "border-error" : ""
                  }
                />
                <Input
                  label="District/City Subdivision"
                  value={formData.buyer?.address?.citySubdivisionName || ""}
                  onChange={(e) =>
                    updateFormField(
                      "buyer.address.citySubdivisionName",
                      e.target.value,
                    )
                  }
                  error={validationErrors.buyerCitySubdivision}
                  required
                  className={
                    validationErrors.buyerCitySubdivision ? "border-error" : ""
                  }
                />
              </div>
            </motion.div>

            {/* VAT Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.vatBreakdown")}
              </h2>
              {formData.vatBreakdown?.map((vat, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <Input
                    label="Taxable Amount"
                    type="number"
                    value={vat.taxableAmount || ""}
                    onChange={(e) => {
                      const newVatBreakdown = [...formData.vatBreakdown];
                      newVatBreakdown[index] = {
                        ...newVatBreakdown[index],
                        taxableAmount: parseFloat(e.target.value),
                      };
                      setFormData({
                        ...formData,
                        vatBreakdown: newVatBreakdown,
                      });
                    }}
                  />
                  <Input
                    label="Tax Amount"
                    type="number"
                    value={vat.taxAmount || ""}
                    onChange={(e) => {
                      const newVatBreakdown = [...formData.vatBreakdown];
                      newVatBreakdown[index] = {
                        ...newVatBreakdown[index],
                        taxAmount: parseFloat(e.target.value),
                      };
                      setFormData({
                        ...formData,
                        vatBreakdown: newVatBreakdown,
                      });
                    }}
                  />
                  <Input
                    label="VAT Category Code"
                    value={vat.vatCategoryCode || ""}
                    onChange={(e) => {
                      const newVatBreakdown = [...formData.vatBreakdown];
                      newVatBreakdown[index] = {
                        ...newVatBreakdown[index],
                        vatCategoryCode: e.target.value,
                      };
                      setFormData({
                        ...formData,
                        vatBreakdown: newVatBreakdown,
                      });
                    }}
                  />
                  <Input
                    label="VAT Rate"
                    value={vat.vatRate || ""}
                    onChange={(e) => {
                      const newVatBreakdown = [...formData.vatBreakdown];
                      newVatBreakdown[index] = {
                        ...newVatBreakdown[index],
                        vatRate: e.target.value,
                      };
                      setFormData({
                        ...formData,
                        vatBreakdown: newVatBreakdown,
                      });
                    }}
                  />
                  {vat.exemptionReasonCode && (
                    <>
                      <Input
                        label="Exemption Reason Code"
                        value={vat.exemptionReasonCode || ""}
                        onChange={(e) => {
                          const newVatBreakdown = [...formData.vatBreakdown];
                          newVatBreakdown[index] = {
                            ...newVatBreakdown[index],
                            exemptionReasonCode: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            vatBreakdown: newVatBreakdown,
                          });
                        }}
                      />
                      <Input
                        label="Exemption Reason"
                        value={vat.exemptionReason || ""}
                        onChange={(e) => {
                          const newVatBreakdown = [...formData.vatBreakdown];
                          newVatBreakdown[index] = {
                            ...newVatBreakdown[index],
                            exemptionReason: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            vatBreakdown: newVatBreakdown,
                          });
                        }}
                      />
                    </>
                  )}
                </div>
              ))}
            </motion.div>

            {/* Invoice Totals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.invoiceTotals")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Tax Exclusive Amount"
                  type="number"
                  value={formData.invoiceTotals?.taxExclusiveAmount || ""}
                  onChange={(e) =>
                    updateFormField(
                      "invoiceTotals.taxExclusiveAmount",
                      parseFloat(e.target.value),
                    )
                  }
                  error={validationErrors.taxExclusiveAmount}
                  required
                  className={
                    validationErrors.taxExclusiveAmount ? "border-error" : ""
                  }
                />
                <Input
                  label="Tax Inclusive Amount"
                  type="number"
                  value={formData.invoiceTotals?.taxInclusiveAmount || ""}
                  onChange={(e) =>
                    updateFormField(
                      "invoiceTotals.taxInclusiveAmount",
                      parseFloat(e.target.value),
                    )
                  }
                  error={validationErrors.taxInclusiveAmount}
                  required
                  className={
                    validationErrors.taxInclusiveAmount ? "border-error" : ""
                  }
                />
                <Input
                  label="Payable Amount"
                  type="number"
                  value={formData.invoiceTotals?.payableAmount || ""}
                  onChange={(e) =>
                    updateFormField(
                      "invoiceTotals.payableAmount",
                      parseFloat(e.target.value),
                    )
                  }
                  error={validationErrors.payableAmount}
                  required
                  className={
                    validationErrors.payableAmount ? "border-error" : ""
                  }
                />
              </div>
            </motion.div>

            {/* Invoice Lines */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.invoiceLines")}
              </h2>
              <div className="space-y-4">
                {formData.invoiceLines?.map((line, index) => (
                  <div
                    key={index}
                    className="p-4 border border-border rounded-lg"
                  >
                    <h3 className="font-medium mb-3">Line {index + 1}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Input
                        label="Line ID (Auto-filled)"
                        value={line.lineId || ""}
                        disabled
                        className="bg-gray-100"
                      />
                      <div className="md:col-span-1 lg:col-span-3">
                        <Input
                          label="Item Description"
                          value={line.item?.name || ""}
                          onChange={(e) =>
                            updateLineItem(index, "item.name", e.target.value)
                          }
                          error={validationErrors[`line${index}Name`]}
                          required
                          className={
                            validationErrors[`line${index}Name`]
                              ? "border-error"
                              : ""
                          }
                        />
                      </div>
                      <Input
                        label="Quantity"
                        type="number"
                        value={line.invoicedQuantity || ""}
                        onChange={(e) =>
                          updateLineItem(
                            index,
                            "invoicedQuantity",
                            parseFloat(e.target.value),
                          )
                        }
                        error={validationErrors[`line${index}Quantity`]}
                        required
                        className={
                          validationErrors[`line${index}Quantity`]
                            ? "border-error"
                            : ""
                        }
                      />
                      <Input
                        label="Unit Price"
                        type="number"
                        value={line.price?.priceAmount || ""}
                        onChange={(e) =>
                          updateLineItem(
                            index,
                            "price.priceAmount",
                            parseFloat(e.target.value),
                          )
                        }
                        error={validationErrors[`line${index}Price`]}
                        required
                        className={
                          validationErrors[`line${index}Price`]
                            ? "border-error"
                            : ""
                        }
                      />
                      <Input
                        label="Unit Code (Auto-filled)"
                        value={line.unitCode || "PCE"}
                        disabled
                        className="bg-gray-100"
                      />
                      <Input
                        label="Line Total (excl. Tax)"
                        type="number"
                        value={line.lineExtensionAmount?.toFixed(2) || "0.00"}
                        disabled
                        className="bg-gray-100"
                      />
                      <Input
                        label="Tax Amount"
                        type="number"
                        value={line.taxTotal?.taxAmount?.toFixed(2) || "0.00"}
                        disabled
                        className="bg-gray-100"
                      />
                      <Input
                        label="Total (incl. Tax)"
                        type="number"
                        value={(
                          (line.lineExtensionAmount || 0) +
                          (line.taxTotal?.taxAmount || 0)
                        ).toFixed(2)}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <div className="flex gap-4 justify-end">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  variant="primary"
                  iconName="Send"
                  onClick={handleSubmit}
                  disabled={hasErrors || isSubmitting}
                  className={hasErrors ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {isSubmitting
                    ? t("invoiceCorrection.submitting")
                    : t("invoiceCorrection.submit")}
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Validation Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 sticky top-8"
            >
              <h2 className="text-xl font-semibold mb-4">
                {t("invoiceCorrection.validationStatus")}
              </h2>

              {/* Backend Errors Section */}
              {backendErrors && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2 mb-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">
                      !
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-1">
                        {t("invoiceCorrection.validationFailed")}
                      </h3>
                      <p className="text-xs text-red-600 dark:text-red-300">
                        {t("invoiceCorrection.fixHighlighted")}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setBackendErrors(null);
                        // Clear backend-added field errors
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          if (backendErrors.missingFields) {
                            backendErrors.missingFields.forEach(({ field }) => {
                              const fieldMap = {
                                "buyer.address.citySubdivisionName":
                                  "buyerCitySubdivision",
                                "buyer.address.streetName": "buyerStreet",
                                "buyer.address.cityName": "buyerCity",
                                "buyer.address.postalZone": "buyerPostalCode",
                                "buyer.address.buildingNumber":
                                  "buyerBuildingNumber",
                                "buyer.registrationName": "buyerName",
                                "buyer.vatNumber": "buyerVatNumber",
                                "buyer.registrationNumber.id": "buyerCrNumber",
                                "seller.address.citySubdivisionName":
                                  "sellerCitySubdivision",
                                "seller.address.streetName": "sellerStreet",
                                "seller.address.cityName": "sellerCity",
                                "seller.address.postalZone": "sellerPostalCode",
                                "seller.address.buildingNumber":
                                  "sellerBuildingNumber",
                                "seller.registrationName": "sellerName",
                                "seller.vatNumber": "sellerVatNumber",
                                "seller.registrationNumber.id":
                                  "sellerCrNumber",
                                "paymentMeans.0.payeeFinancialAccount.iban":
                                  "iban",
                                "invoiceTotals.taxExclusiveAmount":
                                  "taxExclusiveAmount",
                                "invoiceTotals.taxInclusiveAmount":
                                  "taxInclusiveAmount",
                                "invoiceTotals.payableAmount": "payableAmount",
                              };
                              const frontendField = fieldMap[field] || field;
                              delete newErrors[frontendField];
                            });
                          }
                          return newErrors;
                        });
                      }}
                      className="flex-shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-300"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Display missing fields from backend */}
                  {backendErrors.missingFields &&
                    backendErrors.missingFields.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase mb-2">
                          {t("invoiceCorrection.missingRequiredFields")}
                        </p>
                        {backendErrors.missingFields.map((item, idx) => {
                          // Convert field path to readable label
                          const getFieldLabel = (field) => {
                            const labels = {
                              "buyer.address.citySubdivisionName":
                                "Buyer District/City Subdivision",
                              "buyer.address.streetName": "Buyer Street Name",
                              "buyer.address.cityName": "Buyer City",
                              "buyer.address.postalZone": "Buyer Postal Code",
                              "buyer.address.buildingNumber":
                                "Buyer Building Number",
                              "buyer.registrationName": "Buyer Name",
                              "buyer.vatNumber": "Buyer VAT Number",
                              "seller.address.citySubdivisionName":
                                "Seller District/City Subdivision",
                              "seller.address.streetName": "Seller Street Name",
                              "seller.address.cityName": "Seller City",
                              "seller.address.postalZone": "Seller Postal Code",
                              "seller.address.buildingNumber":
                                "Seller Building Number",
                              "seller.registrationName": "Seller Name",
                              "seller.vatNumber": "Seller VAT Number",
                            };
                            return (
                              labels[field] ||
                              field
                                .split(".")
                                .pop()
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())
                            );
                          };

                          return (
                            <div
                              key={idx}
                              className="flex items-start gap-2 p-2 bg-red-100 dark:bg-red-900/30 rounded"
                            >
                              <svg
                                className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <div className="flex-1">
                                <p className="text-xs font-medium text-red-700 dark:text-red-300">
                                  {getFieldLabel(item.field)}
                                </p>
                                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                  {item.message}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              )}

              {/* Frontend Validation Errors */}
              {hasErrors ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-orange-700 dark:text-orange-400 mb-2">
                    {t("invoiceCorrection.formIssues")}
                  </h3>
                  {Object.entries(validationErrors).map(([field, error]) => (
                    <div
                      key={field}
                      className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg"
                    >
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-1">
                        {field
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-300">
                        {error}
                      </p>
                    </div>
                  ))}
                </div>
              ) : !backendErrors ? (
                <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                  <p className="text-success font-medium">
                    ✓ {t("invoiceCorrection.validationsPassed")}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("invoiceCorrection.ready")}
                  </p>
                </div>
              ) : null}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InvoiceErrorCorrection = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <InvoiceErrorCorrectionContent />
      </div>
    </SidebarProvider>
  );
};

export default InvoiceErrorCorrection;
